import { Module, Zone } from "@apis/index";
import Button from "@components/button";
import Column from "@components/column";
import Fields from "@components/fields";
import DropDown from "@components/fields/dropdown";
import Row from "@components/row";
import { RootState } from "@store/index";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import * as Yup from "yup";

interface FormValues {
  zones: { lat: number; lng: number }[][];
  name: string;
  module: any[];
}

const ZoneForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { zoneId } = useParams();
  const { data: modules } = useSelector((state: RootState) => state.modules);
  const { data: zone } = useSelector((state: RootState) => state.singleZone);
  const { data: zones } = useSelector((state: RootState) => state.zones);

  const fetchZones = async () => {
    await Zone.list({ state: "zones" });
  };

  useEffect(() => {
    fetchZones();
  }, []);
  const [initialValues, setInitialValues] = useState<FormValues>({
    zones: [],
    name: "",
    module: [],
  });
  const [loading, setLoading] = useState(true);

  // Fetch modules
  const fetchModules = async () => {
    await Module.list({ state: "modules" });
  };

  // Fetch single zone if editing
  const fetchZone = async () => {
    if (!zoneId) return;
    await Zone.find({ state: "singleZone", id: zoneId });
  };

  useEffect(() => {
    setLoading(true);
    fetchModules();
    fetchZone().finally(() => setLoading(false));

    // Cleanup: clear singleZone on unmount
    return () => {
      dispatch({ type: "singleZone/clear" }); // <-- you should have a Redux action to clear singleZone
    };
  }, [zoneId, dispatch]);

  // Update initial values when zone data is loaded
  useEffect(() => {
    if (zoneId && zone) {
      setInitialValues({
        zones:
          zone.geometry?.map((path: Array<[number, number]>) =>
            path.map(([lng, lat]) => ({ lat, lng }))
          ) || [],
        name: zone.name || "",
        module: zone?.modules?.map((z) => z.id),
      });
    } else if (!zoneId) {
      setInitialValues({ zones: [], name: "", module: [] });
    }
  }, [zoneId, zone]);

  const handleSubmit = async (values: FormValues, { resetForm }) => {
    const payload = {
      name: values.name,
      moduleIds: values.module,
      geometry: values.zones.map((path) =>
        path.map((point) => [point.lng, point.lat])
      ),
    };

    if (zoneId) {
      await Zone.update({ state: "zones", id: zoneId, data: payload }).then(
        () => {
          fetchZones();
        }
      );
    } else {
      await Zone.create({ state: "zones", data: payload }).then(() => {
        fetchZones();
      });
    }

    navigate("/admin/zone-setup");
    resetForm();
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required("Zone name is required")
      .min(3, "Zone name must be at least 3 characters"),
    module: Yup.array().required("Please select at least one module"),
    zones: Yup.array()
      .of(
        Yup.array().of(
          Yup.object().shape({
            lat: Yup.number().required(),
            lng: Yup.number().required(),
          })
        )
      )
      .min(1, "Please draw at least one zone on the map"),
  });

  if (loading) return <p>Loading...</p>;

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ resetForm, values }) => {
        console.log(values.zones, "in");
        return (
          <Form>
            <Column>
              <Row>
                <Fields.Input
                  name="name"
                  placeholder="Write a Zone Name"
                  label="Display Zone Name"
                />

                <div style={{ width: "96.5%" }}>
                  <DropDown
                    selectable={true}
                    isMultiple={true}
                    name="module"
                    options={modules}
                    titleAsKey="name"
                    valueAsKey="id"
                    label="Select Module"
                    placeholder="Select Module"
                    styleInput="width:96%"
                    styleOptionsWrapper="width:101.5%;border-radius:8px"
                  />
                </div>
              </Row>
              <Fields.Map
                height="480px"
                name="zones"
                label="Draw Zones"
                placeholder="Click on the map to draw polygons, double-click to finish a polygon"
              />

              <Button type="submit" title="Submit" justify="end" />
            </Column>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ZoneForm;
